import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DfApiConfigService } from '@df/api/config/df-api-config.service';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Status } from '@df/core/status';
import { MuiInputDirective } from '@df/legacy/ui/form/mui-input.directive';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { OrderByPipe } from '@df/ui/common/order-by.pipe';
import { RadioGroupDirective } from '@df/ui/form/radio/radio.component.def';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentField } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import { Swiper, SwiperEvents } from 'swiper/types';
import { ProfileColorSelectorComponent } from '../../../account/pwa/profiles/color-selector/profile-color-selector.component';
import { ProfileGroupOptionComponent } from '../../../account/pwa/profiles/group-option/profile-group-option.component';
import { HorseProfileColorSelectorComponent } from '../../../account/pwa/profiles/horse-color-selector/horse-color-selector.component';
import { ProfileImageComponent } from '../../../account/pwa/profiles/profile-image/profile-image.component';
import { NProfiles } from '../../../account/pwa/profiles/profile.interfaces';
import { ProfileService } from '../../../account/pwa/profiles/profile.service';
import { ProfileSizeOptionsComponent } from '../../../account/pwa/profiles/size-options/profile-size-options.component';
import { ProfileSizeSelectorComponent } from '../../../account/pwa/profiles/size-selector/profile-size-selector.component';
import { LocalCustomerModel } from '../../../customer/customer.model.decorator';
import { UserImageRole, UserImagesService } from '../../../customer/user-images.service';
import { IconsOptions } from '../../../styles/cms.config';
import { ActionComponent } from '../../../ui/api/action.component';
import { PushCountriesToFrontPipe } from '../../../ui/form/address-fieldset/push-countries-to-front.pipe';
import { CheckboxComponent } from '../../../ui/form/checkbox';
import { InputWrapBodyDirective } from '../../../ui/form/input-wrap-body.directive';
import { InputWrapComponent } from '../../../ui/form/input-wrap.component';
import { RadioComponent } from '../../../ui/form/radio';
import { ValidateDobRangeDirective } from '../../../ui/form/validate/validate-dob-range.directive';
import { ResultComponent } from '../../../ui/resut/result.component';
import { NAntavo } from '../../antavo/antavo.interface';
import { AntavoService } from '../../antavo/antavo.service';
import { IMissionSuccessContent, MISSION_SUCCESS_FIELDS, MissionSuccessComponent } from '../mission-success/mission-success.component';

declare module '@df/session/customer/customer.interfaces' {
    namespace Customer {
        interface ICustomerData {
            profile_mission_data?: string; // JSON string of profile mission progress
        }
    }
}

export namespace NProfileMission {
    export const componentId = 'profile-mission';

    export type IBlockData = Catalog.IBlockData<IProfileMissionContent>;

    export interface IProfileMissionContent extends IMissionSuccessContent {
        slides: ISlideContent[];
    }

    export interface ISlideContent {
        title: string;
        userIcon?: string;
        saveButtonText?: string;
    }
}

@Component({
    selector: 'profile-mission',
    templateUrl: './profile-mission.component.html',
    styleUrls: ['./profile-mission.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        ICmsClientModule,
        RouterModule,
        CarouselModule,
        ImageModule,
        FormsModule,
        InputWrapComponent,
        InputWrapBodyDirective,
        CheckboxComponent,
        MuiInputDirective,
        ActionComponent,
        ProfileColorSelectorComponent,
        ProfileImageComponent,
        ProfileSizeSelectorComponent,
        MissionSuccessComponent,
        ResultComponent,
        ProfileGroupOptionComponent,
        HorseProfileColorSelectorComponent,
        ProfileSizeOptionsComponent,
        RadioGroupDirective,
        RadioComponent,
        OrderByPipe,
        PushCountriesToFrontPipe,
        ValidateDobRangeDirective
    ]
})
@ICmsComponent<ProfileMissionComponent>({
    id: NProfileMission.componentId,
    block: {
        url: NProfileMission.componentId
    },
    onDemandOnly: true,
    name: 'Profile Mission',
    fields: [
        {
            key: 'info',
            type: NICmsContentField.EFieldType.label,
            title: 'Info',
            description:
                'Mission id set in account profiles, person profile settings from member edit, horse profile settings from horse edit'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'slides',
            title: 'Slide Contents',
            itemName: 'Slide',
            itemNameField: 'title',
            maxItems: 7,
            minItems: 7,
            required: true,
            fields: [
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title',
                    required: true
                },
                <NICmsContentField.IChoiceField>{
                    key: 'userIcon',
                    title: 'User Icon',
                    type: NICmsContentField.EFieldType.choice,
                    options: IconsOptions,
                    previewMode: 'icon',
                    default: 'icon-profile'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'saveButtonText',
                    title: 'Save Button Text',
                    default: 'Save'
                }
            ],
            default: [
                {
                    title: "Let's Start",
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                },
                {
                    title: 'All About You',
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                },
                {
                    title: 'All About You',
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                },
                {
                    title: 'Tell Us About Your Ride',
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                },
                {
                    title: 'Tell Us About Your Ride',
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                },
                {
                    title: 'Tell us About Your Horse',
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                },
                {
                    title: 'What are your favourite colours?',
                    userIcon: 'icon-profile',
                    saveButtonText: 'Save'
                }
            ]
        },
        ...MISSION_SUCCESS_FIELDS
    ]
})
export class ProfileMissionComponent extends UiComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: NProfileMission.IProfileMissionContent;

    protected mission!: NAntavo.IChallenge;
    protected profileFormSettings?: NProfiles.IHorseEditContent & NProfiles.IMemberEditContent;
    protected profileData: NProfiles.IProfileData = {
        customer: NProfiles.defaultMemberProfile,
        horses: [NProfiles.defaultHorseProfile]
    };
    protected swiper?: Swiper;
    protected continueUrl: string = '/account/my-missions';
    protected saveStatus: Status | undefined = new Status();
    protected currentSlideIndex = 0;
    protected totalSlides = 8;
    protected otherDiscipline: boolean = false;
    protected profileImage: File | undefined;
    protected horseImage: File | undefined;

    // Form data for different slides
    protected ECompeteOptions = NProfiles.ECompeteOptions;
    protected colourAttributeCode = NProfiles.COLOUR_ATTRIBUTE_CODE;
    protected countries: Nexus.Lib.General.Country[] = [];
    private _horseDob: Date | undefined;
    private route = inject(ActivatedRoute);
    private antavoService = inject(AntavoService);
    private profileService = inject(ProfileService);
    private customerModel = inject(LocalCustomerModel);
    private dfApiConfigService = inject(DfApiConfigService);
    private userImagesService = inject(UserImagesService);

    override async ngOnInit() {
        super.ngOnInit();
        this.mission = this.route.snapshot.data['profileMission'] as NAntavo.IChallenge;
        this.profileFormSettings = {
            ...((this.route.snapshot.data['memberEdit'] as NProfiles.IMemberEditContent) || {}),
            ...((this.route.snapshot.data['horseEdit'] as NProfiles.IHorseEditContent) || {})
        } as NProfiles.IMemberEditContent & NProfiles.IHorseEditContent;
        this.profileData = { ...this.profileData, ...this.customerModel.customerProfile };
        this.otherDiscipline = !!this.profileData.customer?.otherDiscipline;

        // Initialize _horseDob from profileData
        if (this.profileData.horses && this.profileData.horses[0] && this.profileData.horses[0].dateOfBirth) {
            this._horseDob = new Date(this.profileData.horses[0].dateOfBirth);
        } else {
            this._horseDob = undefined;
        }

        this.syncCountries();
    }

    private syncCountries(): void {
        this.countries = [...(this.dfApiConfigService.countries || [])]
            .filter(i => typeof i.name === 'string' && i.name)
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    protected onAfterSwiperInit(eventParams: Parameters<SwiperEvents['afterInit']>): void {
        const [swiper] = eventParams;
        this.swiper = swiper;
    }

    protected onSlideChange(eventParams: Parameters<SwiperEvents['slideChange']>): void {
        const [swiper] = eventParams;
        this.currentSlideIndex = swiper.activeIndex;
    }

    protected async nextSlide(form: NgForm, profileImage?: File, horseImage?: File): Promise<void> {
        if (!form) {
            return;
        }

        form.submitAttempt = true;
        if (form.valid) {
            await this.saveProfileData(form, profileImage, horseImage);
            if (!this.saveStatus?.error && this.swiper && this.currentSlideIndex < this.totalSlides - 1) {
                // reset status to avoid busy animation on next slide
                this.saveStatus = undefined;
                this.detectChanges();
                this.slideNext();
                this.saveStatus = new Status();
            }
        }
    }

    protected slideNext(): void {
        if (!this.swiper) {
            return;
        }
        if (this.currentSlideIndex === 2 && !this.profileData.customer?.isRiding) {
            // Skip to slide 5 if user doesn't ride
            this.swiper.slideTo(4);
        } else if (this.currentSlideIndex === 4 && !this.profileData.customer?.isHorseOwner) {
            // Skip to slide 7 if user doesn't own a horse
            this.swiper.slideTo(6);
        } else {
            this.swiper.slideNext();
        }
    }

    protected async completeMission(): Promise<void> {
        // Save profile data to customer profile
        await this.saveProfileData();

        // Send Antavo mission completion event
        await this.sendMissionCompletionEvent();

        if (this.saveStatus?.error) {
            return;
        }
        // Navigate to success slide
        if (this.swiper) {
            this.swiper.slideTo(this.totalSlides - 1);
        }
    }

    private async saveProfileData(form?: NgForm, profileImage?: File, horseImage?: File): Promise<void> {
        if (profileImage && this.customerModel.loyaltyId) {
            await this.userImagesService.saveUserImage(this.customerModel.loyaltyId, UserImageRole.Profile, profileImage, this.saveStatus);
        }

        if (horseImage && this.customerModel.loyaltyId) {
            await this.userImagesService.saveUserImage(this.customerModel.loyaltyId, UserImageRole.Horse, horseImage, this.saveStatus, 0);
        }

        return this.profileService.saveProfiles(this.profileData, this.saveStatus || new Status(), form, false);
    }

    private async sendMissionCompletionEvent(): Promise<void> {
        try {
            await this.antavoService.finishSimpleMission(this.mission, 'profile_completion', this.saveStatus || new Status());
        } catch (error) {
            console.error('Failed to send mission completion event:', error);
        }
    }

    protected getSlideContent(index: number): NProfileMission.ISlideContent | undefined {
        return this.content?.slides?.[index];
    }

    protected isLastSlide(): boolean {
        return this.currentSlideIndex === this.totalSlides - 1;
    }

    protected isSuccessSlide(): boolean {
        return this.currentSlideIndex === this.totalSlides - 1;
    }

    protected onProfileFileChange(file: File | undefined): void {
        this.profileImage = file;
    }

    protected onHorseFileChange(file: File | undefined): void {
        this.horseImage = file;
    }

    get horseDob(): Date | undefined {
        return this._horseDob;
    }

    set horseDob(dob: Date | undefined) {
        this._horseDob = dob;
        if (this.profileData.horses && this.profileData.horses[0]) {
            this.profileData.horses[0].dateOfBirth = dob && formatDate(dob, 'yyyy-MM-dd', 'en_US');
        }
    }

    get imageRole(): UserImageRole {
        return UserImageRole.Profile;
    }

    get horseImageRole(): UserImageRole {
        return UserImageRole.Horse;
    }
}
